export interface IconMarkers {
    color: string;
    path: string;
    tx?: number;
    svg?: string;
}

export interface IconSymbol {
    name: string;
    path: string;
    tx: number;
    ty: number;
}

export const svgStringToURL = (svg: string): string => {
    // return `data:image/svg+xml;base64,${btoa(svg)}`;
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
};

export const iconSvgBuilder = (
    path: string,
    color: string,
    width = 40,
    height = 36
) => {
    return `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" width="${width}" height="${height}">
        <path fill="${color}" d="${path}"/>
    </svg>`;
};

const mainIconSvgBuilder = (icon: IconSymbol, color: string, middle = 50) => {
    const tx = middle - 30 + icon.tx;
    const ty = middle - 24 + icon.ty;

    return `<g transform="translate(${tx}, ${ty})">${iconSvgBuilder(
        icon.path,
        color,
        60,
        48
    )}</g>`;
};

const buildMarkers = (markers: IconMarkers[]) => {
    const icons = markers.map(
        (marker, index) =>
            `<g transform="translate(${
                72 + index * 32 + (marker.tx || 0)
            }, 13)">${
                marker?.svg
                    ? marker.svg
                    : iconSvgBuilder(marker.path, marker.color)
            }</g>`
    );

    return `
    <rect x="62" y="8" rx="23" ry="23" width="${
        26 + markers.length * 30
    }" height="46" filter="url(#shadow)" fill="#FFFFFF" stroke="stroke-color-name" 
        stroke-width="stroke-width-in-pixels"/>
    ${icons.join('\n')}`;
};

export const iconAnchor = (size: number) => {
    const x = size / 2;
    const y = size / 2;

    return new google.maps.Point(x, y);
};

export const iconBuilder = (
    color: string,
    reverse: boolean,
    icon?: IconSymbol,
    markers?: IconMarkers[],
    size = 100,
    includeRainbowCircle = false,
    isDarkPinIcon = false
) => {
    if (markers && markers.length > 2) {
        throw new Error('Too many markers');
    }

    const hasMarkers = markers && markers.length > 0;

    let mainColor: string;
    let secondaryColor: string;

    if (isDarkPinIcon) {
        // For dark pin icons, use the color for background and black for the icon
        mainColor = color;
        secondaryColor = '#000000';
    } else {
        // Original logic for other icons
        mainColor = reverse ? '#FFFFFF' : color;
        secondaryColor = reverse ? color : '#FFFFFF';
    }

    let width = 100 + (hasMarkers ? markers.length! * 25 : 0);
    let height = hasMarkers ? 120 : 100;
    if (includeRainbowCircle) {
        width += 20;
        height += 20;
    }
    const scalingFactor = size / 100;

    // Size is based on circle, and it is 100x100
    let sizeX = width * scalingFactor;
    const sizeY = height * scalingFactor;
    if (includeRainbowCircle) {
        sizeX += 20;
    }
    // Scale the outer stroke to be an apparent 2px no matter the size of the icon
    const strokeSize = 2 / scalingFactor;

    const canvasWidth = width + 2 * strokeSize;
    const canvasHeight = height + 2 * strokeSize;

    const middle = 50 + strokeSize;

    // show either a rainbow or a white circle around the icon
    const rainbow = includeRainbowCircle
        ? '<circle cx="53" cy="53" r="55" stroke="url(#paint0_linear_3506_1389)" stroke-width="15" fill="none"/>'
        : '';

    const isPinIcon = icon?.name === 'Pin Dark' || icon?.name === 'Pin Light';

    let whiteCircle = '';
    if (includeRainbowCircle) {
        whiteCircle = `<circle cx="53" cy="53" r="45" stroke="${secondaryColor}" stroke-width="5" fill="none"/>`;
    } else if (!isPinIcon) {
        // Don't add border for pin icons
        whiteCircle = `<circle cx="${middle}" cy="${middle}" r="${
            50 + strokeSize / 2
        }" fill="transparent" stroke="${secondaryColor}" stroke-width="${strokeSize}" filter="url(#shadow)"/>`;
    }

    return `
<svg viewBox="0 0 ${canvasWidth} ${canvasHeight}" width="${
        sizeX + 4
    }" height="${sizeY + 4}" xmlns="http://www.w3.org/2000/svg">
 
    
    <g transform="translate(0,${hasMarkers || includeRainbowCircle ? 15 : 0})">
        <circle cx="${middle}" cy="${middle}" r="50" fill="${mainColor}" filter="url(#shadow)"/>
        ${icon ? mainIconSvgBuilder(icon, secondaryColor, middle) : ''}
        ${whiteCircle}
        ${rainbow}     
    </g>

    ${markers && markers.length > 0 ? buildMarkers(markers) : ''}
    <defs>
        <filter id="shadow" x="-20%" y="-10%" width="140%" height="140%">
            <feDropShadow dx="0" dy="0" stdDeviation="3" flood-color="#000000" flood-opacity="0.4"/>
        </filter>
           <filter id="filter0_d_3506_1389" x="0" y="0" width="58" height="58" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
      <feOffset/>
      <feGaussianBlur stdDeviation="2"/>
      <feComposite in2="hardAlpha" operator="out"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.4 0"/>
      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3506_1389"/>
      <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_3506_1389" result="shape"/>
    </filter>
    <linearGradient id="paint0_linear_3506_1389" x1="7.25" y1="22.25" x2="29" y2="44" gradientUnits="userSpaceOnUse">
      <stop stop-color="#AA68FF"/>
      <stop offset="1" stop-color="#51A4FF"/>
    </linearGradient>
  
    </defs>
</svg>
`;
};
